import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PsButtonTuner } from 'stencilSetup';
import { SearchResults } from './SearchResultsPCPDrawer/SearchResults';
import { api } from 'theSource/utils';
import { useConfigurationQuery } from 'theSource/queries';
import { Loader } from 'theSource/components';
import {
  GoogleMapPositionStyled,
  SearchProviderButtonStyled,
} from './ChoosePCPDrawerStyled';
import { GoogleMap } from './GoogleMap';
import { BenefitPlanDto, PcpMembersDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import { AdditionalPlanElectionDataDto } from '../../types';
import { PageContentQueryResponseDto } from 'enrollment/queries/usePageContentQuery/types';

interface SearchProviderProps {
  disabledSearchProviderBtn: boolean;
  locationInput: string;
  milesValue: number;
  providerNameValue: string;
  officeNameValue: string;
  providerData: any;
  setProviderData: any;
  inNetworkValue: boolean;
  acceptNewPatientValue: boolean;
  specialityValue: string[];
  electPlan?: () => void;
  setPcpElectionData: (data: AdditionalPlanElectionDataDto) => void;
  setPcpDrawerRequired: (value: boolean) => void;
  fromPlanDetails?: boolean;
  selectedDependentIds?: number[];
  openChooseProviderDrawer: boolean;
  setOpenChooseProviderDrawer: (value: boolean) => void;
  setShowPcpDrawer: (value: boolean) => void;
  setDisabledSearchProviderBtn: (value: boolean) => void;
  plan?: BenefitPlanDto;
  hideDependentCheckboxWhenChecked: { [key: string]: boolean };
  setHideDependentCheckboxWhenChecked: (value: { [key: string]: boolean }) => void;
  selected: boolean;
  pcpMembers: PcpMembersDto;
  isEsignPlanRequired?: boolean;
  pageContent: PageContentQueryResponseDto | undefined;
}
const isMobile = window.innerWidth < 744;

export const SearchProvider = ({
  disabledSearchProviderBtn,
  locationInput,
  milesValue,
  providerNameValue,
  officeNameValue,
  providerData,
  setProviderData,
  inNetworkValue,
  acceptNewPatientValue,
  specialityValue,
  electPlan,
  setPcpElectionData,
  setPcpDrawerRequired,
  fromPlanDetails = false,
  selectedDependentIds,
  openChooseProviderDrawer,
  setOpenChooseProviderDrawer,
  setShowPcpDrawer,
  setDisabledSearchProviderBtn,
  plan,
  hideDependentCheckboxWhenChecked,
  setHideDependentCheckboxWhenChecked,
  selected,
  pcpMembers,
  isEsignPlanRequired,
  pageContent,
}: SearchProviderProps) => {
  const [loader, setLoader] = useState<boolean>(false);
  const [loadMap, setLoadMap] = useState<boolean>(false);
  const [noProviderResultText, setNoProviderResultText] = useState('');

  const isWideViewport = window.innerWidth > 1023;
  const mapStyle = isWideViewport
    ? { width: '376px', height: '330px', marginTop: '36px' }
    : { width: '239px', height: '209px', marginTop: '36px' };

  const { t } = useTranslation('enrollment.ChoosePCPDrawer');

  const { configuration } = useConfigurationQuery();

  const lumosApiHost = configuration?.apiHost;

  const specialityValueJoin = specialityValue.join(','); 

  const handleSearchProvider = () => {
    setLoader(true);
    setLoadMap(false);

    const params = {
      ...(providerNameValue && { provider_name: providerNameValue }),
      ...(officeNameValue && { office_name: officeNameValue }),
      ...(inNetworkValue && { in_network: inNetworkValue }),
      ...(acceptNewPatientValue && { accepting_new: acceptNewPatientValue }),
    };
    
    let url = `${lumosApiHost}/v1/self_service/primary_care_providers/search?org_plan_id=${plan?.plan_id}&zip_code=${locationInput}&distance=${milesValue}`;

    const encodedSpecialities = specialityValueJoin
      .split(',')
      .filter(Boolean)
      .map(encodeURIComponent)
      .join(',');

    if (specialityValueJoin) {
      url += `&specialties=${encodedSpecialities}`;
    }

    api
      .get(url, {
        ...params,
      })
      .then((response: any) => {
        setLoader(false);

        setProviderData(response);
        setLoadMap(true);
        locationInput ? setDisabledSearchProviderBtn(false) : setDisabledSearchProviderBtn(true);

        setNoProviderResultText('No Results Found');
      })
      .catch((err) => {
        console.log(err?.response?.data?.details);
      });
  };

  const markerFromProviders = (): {
    position: { lat: number; lng: number };
    title: string;
    infoPopup: string;
  }[] => {
    let markers: {
      position: { lat: number; lng: number };
      title: string;
      infoPopup: string;
    }[] = [];
    const currentProviders = providerData['providers'];
    if (currentProviders?.length > 0) {
      markers = currentProviders?.map((office: any) => {
        return {
          position: {
            lat: parseFloat(office?.latitude.toString()),
            lng: parseFloat(office?.longitude.toString()),
          },
          title: office.name,
          infoPopup: `<h6>${office?.name}</h6>
                        <div>${office?.office_name}</div>
                        <div>${office?.street_address}</div>
                        <div>${office?.city_state_zip}</div>
                        <div>${office?.phone_number}</div>`,
        };
      });
    }
    return markers;
  };

  return (
    <>
      {isMobile && (
        <div className="d-flex justify-content-end pb-4 ">
          <SearchProviderButtonStyled>
            <PsButtonTuner
              buttonId="search-provider-btn"
              data-testid="search-provider-btn"
              level="primary"
              label={t('dental.searchProvidersButtonLabel')}
              icon="binoculars"
              onClick={handleSearchProvider}
              disabled={disabledSearchProviderBtn}
            />
          </SearchProviderButtonStyled>
        </div>
      )}
      <div className="d-flex justify-content-md-between position-relative">
        {
        loader ? (
            <Loader />
        ) :
        providerData?.providers?.length > 0 ? (
          <div className={`left-column ${isMobile && 'border-top pt-2_5'}`}>
            <h2 className={`mb-md-5  ${isMobile && 'mt-2_5'}`}>
              Search Results
            </h2>
            <SearchResults
              providerData={providerData}
              electPlan={electPlan}
              setPcpElectionData={setPcpElectionData}
              setPcpDrawerRequired={setPcpDrawerRequired}
              fromPlanDetails={fromPlanDetails}
              selectedDependentIds={selectedDependentIds}
              openChooseProviderDrawer={openChooseProviderDrawer}
              setOpenChooseProviderDrawer={setOpenChooseProviderDrawer}
              setShowPcpDrawer={setShowPcpDrawer}
              plan={plan}
              hideDependentCheckboxWhenChecked={hideDependentCheckboxWhenChecked}
              setHideDependentCheckboxWhenChecked={setHideDependentCheckboxWhenChecked}
              selected={selected}
              pcpMembers={pcpMembers}
              isEsignPlanRequired={isEsignPlanRequired}
              pageContent={pageContent}
            />
          </div>
        ) : (
          <h3>{noProviderResultText}</h3>
        )}
        {!isMobile && (
          <>
            <div className="right-column">
              <SearchProviderButtonStyled>
                <PsButtonTuner
                  buttonId="search-provider-btn"
                  data-testid="search-provider-btn"
                  level="primary"
                  label={t('dental.searchProvidersButtonLabel')}
                  icon="binoculars"
                  onClick={handleSearchProvider}
                  disabled={disabledSearchProviderBtn}
                />
              </SearchProviderButtonStyled>
              <GoogleMapPositionStyled className="position-sticky">
                {loadMap && providerData?.providers?.length > 0 && (
                  <GoogleMap
                    style={mapStyle}
                    maxInitialZoom={15}
                    id="providerGoogleMap"
                    markers={markerFromProviders()}
                  />
                )}
              </GoogleMapPositionStyled>
            </div>
          </>
        )}
      </div>
    </>
  );
};
